import { GlobalConfigType } from 'actions'
import { FieldOrigin } from 'config'
import type { ICrossCollateralBlanket, ILoanDetail, IMultipleProperty } from 'pages/LoanStructure/interfaces'
import { store } from 'reducers'
import { combinedPropertyMonthlyExpense } from 'services/pdfs/utils'
import { getPrice3decimal, InputValidate, isEmpty, removeComma } from 'utils'
import { isLoanFieldPropHidden } from 'utils/loan'

import { additionalFieldsForAdmin, defaultInputs } from './constant'
import type { IAdditionalPropertyInformation } from './interface'

export const isAdditionalPropertyValidated = (item: IAdditionalPropertyInformation) => {
  if (item.overrideValidation) return true

  const { globalConfigReducer } = store.getState()
  const fieldProperties = globalConfigReducer[GlobalConfigType.LoanFields]?.[FieldOrigin.AdditionalPropertyAddresses] || {}
  const inputs = defaultInputs()
  Object.keys(fieldProperties).forEach((key) => {
    if (!inputs[key]) return
    const prop = fieldProperties[key]
    if (prop.required !== undefined) inputs[key].required = prop.required
    if (isLoanFieldPropHidden(prop)) inputs[key].visible = false
  })
  const hasErrorInputs = !!Object.keys(inputs)
    .filter((key) => !additionalFieldsForAdmin.includes(key))
    .filter((key) =>
      InputValidate({
        ...inputs[key],
        value: (item as any)[key],
      }),
    ).length

  return !hasErrorInputs
}

export const getFailedAdditionalPropertyInfo = (): IAdditionalPropertyInformation[] => {
  const {
    borrower: {
      borrower: { additionalPropertyInfo, crossCollateralLimit, allocatedLoanAmount },
    },
    loanDetail,
    loan,
  } = store.getState()

  if (!loan.isMultipleProperty || isEmpty(loan.multipleProperties)) return []

  const { rateData } = loanDetail as ILoanDetail

  let failedAddresses = []

  if (!isEmpty(crossCollateralLimit) && !!allocatedLoanAmount.length)
    failedAddresses = additionalPropertyInfo.filter((item: IAdditionalPropertyInformation, index: number) => {
      const targetProperty = loan?.multipleProperties?.find(
        (v: IMultipleProperty) => v.subjectPropertyAddress === item.propertyAddress,
      )
      let dscr = 0
      if (!!targetProperty) {
        const pitis = combinedPropertyMonthlyExpense(loan, targetProperty, loanDetail.rateData)
        dscr = removeComma(pitis.dscr)
      }
      return !isPassedCrossCollateralLimit(item, dscr, crossCollateralLimit, allocatedLoanAmount[index], rateData.type)
        .isPassed
    })

  const incompleteAddresses = additionalPropertyInfo.filter(
    (item: IAdditionalPropertyInformation) => !isAdditionalPropertyValidated(item),
  )

  failedAddresses.forEach((item: IAdditionalPropertyInformation) => {
    if (!incompleteAddresses.includes(item) && !item.overrideValidation) incompleteAddresses.push(item)
  })

  return incompleteAddresses
}

export const additionalPropertyInfoValidate = () => {
  return getFailedAdditionalPropertyInfo().length == 0
}

export const isPassedCrossCollateralLimit = (
  data: IAdditionalPropertyInformation,
  dscr: number,
  crossCollateralBlanketLimit: ICrossCollateralBlanket,
  allocatedLoanAmount: number,
  productType: string,
) => {
  let errors: string[] = []

  const {
    propertyLevelAllocatedBalanceLimit,
    eligibleOccupancies,
    eligiblePropertyTypes,
    minAsIsValue,
    minPurchasePrice,
    minDscr,
  } = crossCollateralBlanketLimit
  const { occupancy, propertyType, asIsValue, purchasePrice, propertyCounty } = data

  if (!String(propertyCounty).toLowerCase().includes('county')) errors.push(`Invalid Property County.`)
  if (isEmpty(allocatedLoanAmount)) errors.push(`Calculated Allocated Loan Amount is N/A.`)

  if (
    allocatedLoanAmount < propertyLevelAllocatedBalanceLimit.min ||
    allocatedLoanAmount > propertyLevelAllocatedBalanceLimit.max
  )
    errors.push(
      `Calculated Allocated Loan Amount = ${getPrice3decimal(
        allocatedLoanAmount,
      )}. It should be in range of ${getPrice3decimal(propertyLevelAllocatedBalanceLimit.min)} to ${getPrice3decimal(
        propertyLevelAllocatedBalanceLimit.max,
      )}.`,
    )

  if (!eligibleOccupancies.includes(occupancy)) errors.push(`Occupancy is ineligible.`)

  if (!eligiblePropertyTypes.includes(propertyType)) errors.push(`Property Type is ineligible.`)

  if (removeComma(asIsValue) < minAsIsValue)
    errors.push(`As Is Value should be greater than ${getPrice3decimal(minAsIsValue)}.`)

  if (removeComma(purchasePrice) < minPurchasePrice)
    errors.push(`Purchase Price should be greater than ${getPrice3decimal(minPurchasePrice)}.`)

  const targetDscrLimit = minDscr.find((item) => item.propertyTypes.includes(propertyType))
  if (targetDscrLimit) {
    const isAmortizing = productType.includes('ARM')
    const interestOnly = productType.includes('IO') || productType.toLowerCase().includes('I/O')

    let minDscrValue = 0
    if (isAmortizing) minDscrValue = targetDscrLimit.amortizingPaymentMinDscr
    if (interestOnly && targetDscrLimit.interestOnlyMinDscr > minDscrValue)
      minDscrValue = targetDscrLimit.interestOnlyMinDscr

    if (dscr < minDscrValue) errors.push(`DSCR should be greater than ${minDscrValue}.`)
  }

  return { isPassed: errors.length === 0, errors }
}
